import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bar,
  BarChart,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import Footer from "../components/Footer";
import Sidebar from "../components/Sidebar";
import Topbar from "../components/Topbar";
import { usePageTitle } from "../contexts/PageTitleContext";
import { certificateService } from "../services/api/certificate.service";
import { paymentService } from "../services/api/payment.service";
import { settingsService } from "../services/api/settings.service";
import {
  ApplicationCategory,
  type CertificateApplication,
} from "../types/certificate.types";
import { UserStatusId } from "../types/user.types";

const toDateInputValue = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getTodayDateInput = () => toDateInputValue(new Date());

const getMonthStartDateInput = () => {
  const now = new Date();
  return toDateInputValue(new Date(now.getFullYear(), now.getMonth(), 1));
};

const Dashboard = () => {
  const { setPageTitle } = usePageTitle();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [applications, setApplications] = useState<CertificateApplication[]>(
    [],
  );
  const [countyRangeStart, setCountyRangeStart] = useState<string>(
    getMonthStartDateInput(),
  );
  const [countyRangeEnd, setCountyRangeEnd] =
    useState<string>(getTodayDateInput());
  const [trendRangeStart, setTrendRangeStart] = useState<string>(
    getMonthStartDateInput(),
  );
  const [trendRangeEnd, setTrendRangeEnd] =
    useState<string>(getTodayDateInput());
  const [revenueWedding, setRevenueWedding] = useState<number | null>(null);
  const [revenueDivorce, setRevenueDivorce] = useState<number | null>(null);
  const [defaultCurrency, setDefaultCurrency] = useState<string>("USD");
  const [reprintWeddingCount, setReprintWeddingCount] = useState<number | null>(
    null,
  );
  const [reprintDivorceCount, setReprintDivorceCount] = useState<number | null>(
    null,
  );

  useEffect(() => {
    setPageTitle("Dashboard", "");
    const isDesktop = window.matchMedia("(min-width: 881px)").matches;
    setSidebarOpen(isDesktop);

    const handleResize = () => {
      const isDesktopView = window.matchMedia("(min-width: 881px)").matches;
      setSidebarOpen(isDesktopView);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [setPageTitle]);

  useEffect(() => {
    const fetchCounts = async () => {
      try {
        const [all, payments, settings] = await Promise.all([
          certificateService.getCertificateApplications(),
          paymentService.getPayments(),
          settingsService.getSettings(),
        ]);
        setApplications(all);

        const currencySetting = settings.find(
          (s) =>
            s.category === "general" &&
            (s.key === "defaultCurrency" || s.key === "currency"),
        );
        if (currencySetting?.value?.trim()) {
          setDefaultCurrency(currencySetting.value.trim().toUpperCase());
        }

        const reprint = all.filter((a) => a.statusId === UserStatusId.Printed);
        const appCategoryById = new Map(
          all.map((a) => [a.id, a.category] as const),
        );
        const weddingRevenueTotal = payments
          .filter((payment) => {
            const category =
              payment.certificateApplication?.category ||
              appCategoryById.get(payment.certificateApplicationId);
            return category === ApplicationCategory.Marriage;
          })
          .reduce(
            (sum, payment) =>
              sum + (payment.lraPayment || 0) + (payment.operationalCost || 0),
            0,
          );
        const divorceRevenueTotal = payments
          .filter((payment) => {
            const category =
              payment.certificateApplication?.category ||
              appCategoryById.get(payment.certificateApplicationId);
            return category === ApplicationCategory.Divorce;
          })
          .reduce(
            (sum, payment) =>
              sum + (payment.lraPayment || 0) + (payment.operationalCost || 0),
            0,
          );

        setReprintWeddingCount(
          reprint.filter((a) => a.category === ApplicationCategory.Marriage)
            .length,
        );
        setReprintDivorceCount(
          reprint.filter((a) => a.category === ApplicationCategory.Divorce)
            .length,
        );
        setRevenueWedding(weddingRevenueTotal);
        setRevenueDivorce(divorceRevenueTotal);
      } catch {
        setApplications([]);
        setReprintWeddingCount(0);
        setReprintDivorceCount(0);
        setRevenueWedding(0);
        setRevenueDivorce(0);
        setDefaultCurrency("USD");
      }
    };
    fetchCounts();
  }, []);

  const formatCurrency = (amount: number): string => {
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: defaultCurrency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);
    } catch {
      return `${defaultCurrency} ${amount.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    }
  };

  // Decision-support KPIs derived from the loaded applications.
  const kpis = useMemo(() => {
    const countOf = (ids: number[]) =>
      applications.filter((a) => ids.includes(a.statusId ?? -1)).length;

    const total = applications.length;
    const submittedPlus = applications.filter(
      (a) =>
        a.statusId !== UserStatusId.Created &&
        a.statusId !== UserStatusId.Draft,
    ).length;
    const approvedPlus = countOf([
      UserStatusId.Approved,
      UserStatusId.Verified,
      UserStatusId.Attested,
      UserStatusId.Completed,
      UserStatusId.Printed,
    ]);
    const approvalRate =
      submittedPlus > 0 ? Math.round((approvedPlus / submittedPlus) * 100) : 0;

    return {
      total,
      approvalRate,
      awaitingApproval: countOf([
        UserStatusId.Submitted,
        UserStatusId.Pending,
        UserStatusId.InProgress,
      ]),
      queried: countOf([UserStatusId.Queried]),
      rejected: countOf([UserStatusId.Rejected]),
      completed: countOf([UserStatusId.Completed]),
    };
  }, [applications]);

  // Processing SLA (target: 7 days). "Overdue" = still pending past the target.
  const SLA_DAYS = 7;
  const slaMetrics = useMemo(() => {
    const now = Date.now();
    const dayMs = 1000 * 60 * 60 * 24;
    const pending = applications.filter(
      (a) =>
        a.statusId === UserStatusId.Submitted ||
        a.statusId === UserStatusId.Pending ||
        a.statusId === UserStatusId.InProgress ||
        a.statusId === UserStatusId.Queried,
    );
    const ageDays = (a: CertificateApplication) => {
      const created = a.createdAt ? new Date(a.createdAt).getTime() : now;
      return (now - created) / dayMs;
    };
    const overdue = pending.filter((a) => ageDays(a) > SLA_DAYS).length;
    const avgAge =
      pending.length > 0
        ? Math.round(
            pending.reduce((sum, a) => sum + ageDays(a), 0) / pending.length,
          )
        : 0;
    return { overdue, avgAge, pendingTotal: pending.length };
  }, [applications]);

  // Revenue split for the donut chart.
  const revenueDonut = useMemo(
    () =>
      [
        { name: "Marriage", value: revenueWedding ?? 0, color: "#1e3a8a" },
        { name: "Divorce", value: revenueDivorce ?? 0, color: "#b91c1c" },
      ].filter((d) => d.value > 0),
    [revenueWedding, revenueDivorce],
  );

  // Status breakdown for the pipeline chart.
  const statusBreakdown = useMemo(() => {
    const defs: Array<[number, string, string]> = [
      [UserStatusId.Created, "Created", "#94a3b8"],
      [UserStatusId.Submitted, "Submitted", "#3b82f6"],
      [UserStatusId.InProgress, "In Progress", "#6366f1"],
      [UserStatusId.Queried, "Queried", "#f59e0b"],
      [UserStatusId.Approved, "Approved", "#10b981"],
      [UserStatusId.Completed, "Completed", "#059669"],
      [UserStatusId.Printed, "Printed", "#0ea5e9"],
      [UserStatusId.Rejected, "Rejected", "#ef4444"],
    ];
    const rows = defs.map(([id, name, color]) => ({
      name,
      color,
      count: applications.filter((a) => a.statusId === id).length,
    }));
    const max = Math.max(1, ...rows.map((r) => r.count));
    return { rows: rows.filter((r) => r.count > 0), max };
  }, [applications]);

  const trendChartData = useMemo(() => {
    const startDate = new Date(`${trendRangeStart}T00:00:00`);
    const endDate = new Date(`${trendRangeEnd}T00:00:00`);

    if (
      Number.isNaN(startDate.getTime()) ||
      Number.isNaN(endDate.getTime()) ||
      startDate > endDate
    ) {
      return {
        chartWidth: 600,
        chartHeight: 220,
        padding: { top: 16, right: 20, bottom: 32, left: 30 },
        maxY: 1,
        points: [] as Array<{ date: Date; marriage: number; divorce: number }>,
        weddingLine: "",
        divorceLine: "",
        xForIndex: (_index: number) => 0,
        yForValue: (_value: number) => 0,
        formatLabel: (date: Date) => `${date.getDate()}/${date.getMonth() + 1}`,
        startDate,
        endDate,
      };
    }

    const rangeDays =
      Math.floor((endDate.getTime() - startDate.getTime()) / 86_400_000) + 1;

    const buckets = new Map<
      string,
      { date: Date; marriage: number; divorce: number }
    >();

    for (let index = 0; index < rangeDays; index += 1) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + index);
      const key = date.toISOString().split("T")[0];
      buckets.set(key, { date, marriage: 0, divorce: 0 });
    }

    applications.forEach((application) => {
      if (!application.createdAt) return;
      const createdDate = new Date(application.createdAt);
      createdDate.setHours(0, 0, 0, 0);
      if (createdDate < startDate || createdDate > endDate) return;
      const key = createdDate.toISOString().split("T")[0];
      const bucket = buckets.get(key);
      if (!bucket) return;

      if (application.category === ApplicationCategory.Marriage) {
        bucket.marriage += 1;
      } else if (application.category === ApplicationCategory.Divorce) {
        bucket.divorce += 1;
      }
    });

    const points = Array.from(buckets.values());
    const maxY = Math.max(
      1,
      ...points.map((point) => Math.max(point.marriage, point.divorce)),
    );

    const chartWidth = 600;
    const chartHeight = 220;
    const padding = { top: 16, right: 20, bottom: 32, left: 30 };
    const drawableWidth = chartWidth - padding.left - padding.right;
    const drawableHeight = chartHeight - padding.top - padding.bottom;

    const xForIndex = (index: number) =>
      points.length > 1
        ? padding.left + (index / (points.length - 1)) * drawableWidth
        : padding.left + drawableWidth / 2;

    const yForValue = (value: number) =>
      padding.top + (1 - value / maxY) * drawableHeight;

    const weddingLine = points
      .map((point, index) => `${xForIndex(index)},${yForValue(point.marriage)}`)
      .join(" ");

    const divorceLine = points
      .map((point, index) => `${xForIndex(index)},${yForValue(point.divorce)}`)
      .join(" ");

    const formatLabel = (date: Date) =>
      `${date.getDate()}/${date.getMonth() + 1}`;

    return {
      chartWidth,
      chartHeight,
      padding,
      maxY,
      points,
      weddingLine,
      divorceLine,
      xForIndex,
      yForValue,
      formatLabel,
      startDate,
      endDate,
    };
  }, [applications, trendRangeEnd, trendRangeStart]);

  const countyChartData = useMemo(() => {
    const startDate = new Date(`${countyRangeStart}T00:00:00`);
    const endDate = new Date(`${countyRangeEnd}T23:59:59`);

    if (
      Number.isNaN(startDate.getTime()) ||
      Number.isNaN(endDate.getTime()) ||
      startDate > endDate
    ) {
      return {
        items: [] as Array<{
          county: string;
          marriage: number;
          divorce: number;
          total: number;
        }>,
        maxY: 1,
        chartWidth: 640,
        chartHeight: 260,
        padding: { top: 16, right: 20, bottom: 44, left: 34 },
        groupWidth: 0,
        barWidth: 0,
        barGap: 0,
        labelStep: 1,
      };
    }

    const byCounty = new Map<string, { marriage: number; divorce: number }>();

    applications.forEach((application) => {
      if (!application.createdAt) return;

      const createdAt = new Date(application.createdAt);
      if (Number.isNaN(createdAt.getTime())) return;
      if (createdAt < startDate || createdAt > endDate) return;

      const countyName =
        application.countyName || application.county?.name || "Unspecified";
      const current = byCounty.get(countyName) || { marriage: 0, divorce: 0 };

      if (application.category === ApplicationCategory.Marriage) {
        current.marriage += 1;
      } else if (application.category === ApplicationCategory.Divorce) {
        current.divorce += 1;
      }

      byCounty.set(countyName, current);
    });

    const items = Array.from(byCounty.entries())
      .map(([county, values]) => ({
        county,
        marriage: values.marriage,
        divorce: values.divorce,
        total: values.marriage + values.divorce,
      }))
      .sort(
        (left, right) =>
          right.total - left.total || left.county.localeCompare(right.county),
      );

    const maxY = Math.max(
      1,
      ...items.map((item) => Math.max(item.marriage, item.divorce)),
    );

    const padding = { top: 16, right: 20, bottom: 44, left: 34 };
    const chartWidth = 640;
    const chartHeight = 260;
    const drawableWidth = chartWidth - padding.left - padding.right;
    const groupWidth =
      items.length > 0 ? drawableWidth / items.length : drawableWidth;
    const barWidth = Math.max(4, Math.min(18, groupWidth * 0.32));
    const barGap = Math.max(2, Math.min(10, groupWidth * 0.12));
    const labelStep = Math.max(1, Math.ceil(items.length / 8));

    return {
      items,
      maxY,
      chartWidth,
      chartHeight,
      padding,
      groupWidth,
      barWidth,
      barGap,
      labelStep,
    };
  }, [applications, countyRangeEnd, countyRangeStart]);


  return (
    <div
      className={`dashboard-body ${sidebarOpen ? "sidebar-open" : "sidebar-closed"}`}
    >
      <Topbar
        onMenuToggle={() => setSidebarOpen((prev) => !prev)}
        sidebarOpen={sidebarOpen}
      />
      <Sidebar isOpen={sidebarOpen} />
      <main className="main-content">
        <section className="kpi-strip">
          <div className="kpi-card">
            <span className="kpi-card__label">Total Applications</span>
            <span className="kpi-card__value">
              {kpis.total.toLocaleString()}
            </span>
            <span className="kpi-card__sub">All marriage &amp; divorce</span>
          </div>
          <div className="kpi-card kpi-card--good">
            <span className="kpi-card__label">Approval Rate</span>
            <span className="kpi-card__value">{kpis.approvalRate}%</span>
            <span className="kpi-card__sub">Approved of submitted</span>
          </div>
          <div
            className="kpi-card kpi-card--info kpi-card--clickable"
            onClick={() => navigate("/marriages/approval")}
            role="button"
            tabIndex={0}
          >
            <span className="kpi-card__label">Awaiting Approval</span>
            <span className="kpi-card__value">{kpis.awaitingApproval}</span>
            <span className="kpi-card__sub">In the approval queue →</span>
          </div>
          <div
            className="kpi-card kpi-card--warn kpi-card--clickable"
            onClick={() => navigate("/marriages/approval")}
            role="button"
            tabIndex={0}
          >
            <span className="kpi-card__label">Action Needed (Queried)</span>
            <span className="kpi-card__value">{kpis.queried}</span>
            <span className="kpi-card__sub">Sent back for changes →</span>
          </div>
          <div
            className={`kpi-card ${slaMetrics.overdue > 0 ? "kpi-card--danger" : "kpi-card--good"}`}
          >
            <span className="kpi-card__label">Overdue (&gt;{SLA_DAYS}d)</span>
            <span className="kpi-card__value">{slaMetrics.overdue}</span>
            <span className="kpi-card__sub">
              Avg pending age {slaMetrics.avgAge}d
            </span>
          </div>
          <div className="kpi-card kpi-card--good">
            <span className="kpi-card__label">Completed</span>
            <span className="kpi-card__value">{kpis.completed}</span>
            <span className="kpi-card__sub">Certificates finalized</span>
          </div>
          {kpis.rejected > 0 && (
            <div className="kpi-card kpi-card--danger">
              <span className="kpi-card__label">Rejected</span>
              <span className="kpi-card__value">{kpis.rejected}</span>
              <span className="kpi-card__sub">Declined applications</span>
            </div>
          )}
        </section>

        <section className="content-grid">
          <article className="panel">
            <h3>Certificates Processing Trend</h3>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
                gap: "0.6rem",
                marginBottom: "0.85rem",
              }}
            >
              <label
                style={{
                  display: "grid",
                  gap: "0.2rem",
                  fontSize: "0.82rem",
                  color: "#334155",
                }}
              >
                From
                <input
                  type="date"
                  value={trendRangeStart}
                  max={trendRangeEnd}
                  onChange={(event) => setTrendRangeStart(event.target.value)}
                />
              </label>
              <label
                style={{
                  display: "grid",
                  gap: "0.2rem",
                  fontSize: "0.82rem",
                  color: "#334155",
                }}
              >
                To
                <input
                  type="date"
                  value={trendRangeEnd}
                  min={trendRangeStart}
                  onChange={(event) => setTrendRangeEnd(event.target.value)}
                />
              </label>
            </div>

            <div style={{ width: "100%", overflow: "hidden" }}>
              <svg
                viewBox={`0 0 ${trendChartData.chartWidth} ${trendChartData.chartHeight}`}
                style={{ width: "100%", height: "auto", display: "block" }}
                role="img"
                aria-label="Certificate processing trend line chart"
              >
                {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
                  const y =
                    trendChartData.padding.top +
                    ratio *
                      (trendChartData.chartHeight -
                        trendChartData.padding.top -
                        trendChartData.padding.bottom);
                  return (
                    <line
                      key={`grid-${ratio}`}
                      x1={trendChartData.padding.left}
                      y1={y}
                      x2={
                        trendChartData.chartWidth - trendChartData.padding.right
                      }
                      y2={y}
                      stroke="#e2e8f0"
                      strokeWidth="1"
                    />
                  );
                })}

                {trendChartData.weddingLine && (
                  <polyline
                    fill="none"
                    stroke="#2563eb"
                    strokeWidth="3"
                    points={trendChartData.weddingLine}
                    strokeLinejoin="round"
                    strokeLinecap="round"
                  />
                )}
                {trendChartData.divorceLine && (
                  <polyline
                    fill="none"
                    stroke="#dc2626"
                    strokeWidth="3"
                    points={trendChartData.divorceLine}
                    strokeLinejoin="round"
                    strokeLinecap="round"
                  />
                )}

                {trendChartData.points.map((point, index) => {
                  const x = trendChartData.xForIndex(index);
                  return (
                    <g key={point.date.toISOString()}>
                      <circle
                        cx={x}
                        cy={trendChartData.yForValue(point.marriage)}
                        r="3.5"
                        fill="#2563eb"
                      />
                      <circle
                        cx={x}
                        cy={trendChartData.yForValue(point.divorce)}
                        r="3.5"
                        fill="#dc2626"
                      />
                      {(index === 0 ||
                        index === trendChartData.points.length - 1 ||
                        index %
                          Math.max(
                            1,
                            Math.ceil(trendChartData.points.length / 6),
                          ) ===
                          0) && (
                        <text
                          x={x}
                          y={trendChartData.chartHeight - 8}
                          textAnchor="middle"
                          fontSize="10"
                          fill="#64748b"
                        >
                          {trendChartData.formatLabel(point.date)}
                        </text>
                      )}
                    </g>
                  );
                })}

                <text
                  x={trendChartData.padding.left}
                  y={12}
                  fontSize="10"
                  fill="#64748b"
                >
                  Max/day: {trendChartData.maxY}
                </text>
              </svg>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginTop: "0.5rem",
                gap: "1rem",
                flexWrap: "wrap",
                fontSize: "0.85rem",
              }}
            >
              <div
                style={{ display: "flex", gap: "1rem", alignItems: "center" }}
              >
                <span
                  style={{
                    display: "inline-flex",
                    gap: "0.35rem",
                    alignItems: "center",
                    color: "#1e3a8a",
                  }}
                >
                  <span
                    style={{
                      width: "10px",
                      height: "10px",
                      borderRadius: "999px",
                      background: "#2563eb",
                      display: "inline-block",
                    }}
                  />
                  Marriage
                </span>
                <span
                  style={{
                    display: "inline-flex",
                    gap: "0.35rem",
                    alignItems: "center",
                    color: "#991b1b",
                  }}
                >
                  <span
                    style={{
                      width: "10px",
                      height: "10px",
                      borderRadius: "999px",
                      background: "#dc2626",
                      display: "inline-block",
                    }}
                  />
                  Divorce
                </span>
              </div>
              <span style={{ color: "#64748b" }}>
                {trendChartData.startDate.toLocaleDateString()} -{" "}
                {trendChartData.endDate.toLocaleDateString()}
              </span>
            </div>
          </article>
          <article className="panel">
            <h3>Applications Per County</h3>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
                gap: "0.6rem",
                marginBottom: "0.85rem",
              }}
            >
              <label
                style={{
                  display: "grid",
                  gap: "0.2rem",
                  fontSize: "0.82rem",
                  color: "#334155",
                }}
              >
                From
                <input
                  type="date"
                  value={countyRangeStart}
                  max={countyRangeEnd}
                  onChange={(event) => setCountyRangeStart(event.target.value)}
                />
              </label>
              <label
                style={{
                  display: "grid",
                  gap: "0.2rem",
                  fontSize: "0.82rem",
                  color: "#334155",
                }}
              >
                To
                <input
                  type="date"
                  value={countyRangeEnd}
                  min={countyRangeStart}
                  onChange={(event) => setCountyRangeEnd(event.target.value)}
                />
              </label>
            </div>

            <div style={{ width: "100%", overflow: "hidden" }}>
              <svg
                viewBox={`0 0 ${countyChartData.chartWidth} ${countyChartData.chartHeight}`}
                style={{ width: "100%", height: "auto", display: "block" }}
                role="img"
                aria-label="Applications per county bar chart"
              >
                {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
                  const y =
                    countyChartData.padding.top +
                    ratio *
                      (countyChartData.chartHeight -
                        countyChartData.padding.top -
                        countyChartData.padding.bottom);
                  return (
                    <line
                      key={`county-grid-${ratio}`}
                      x1={countyChartData.padding.left}
                      y1={y}
                      x2={
                        countyChartData.chartWidth -
                        countyChartData.padding.right
                      }
                      y2={y}
                      stroke="#e2e8f0"
                      strokeWidth="1"
                    />
                  );
                })}

                {countyChartData.items.map((item, index) => {
                  const drawableHeight =
                    countyChartData.chartHeight -
                    countyChartData.padding.top -
                    countyChartData.padding.bottom;
                  const groupWidth = countyChartData.groupWidth;
                  const barWidth = countyChartData.barWidth;
                  const gap = countyChartData.barGap;
                  const groupInnerWidth = barWidth * 2 + gap;
                  const xOffset = Math.max(
                    0,
                    (groupWidth - groupInnerWidth) / 2,
                  );
                  const xStart =
                    countyChartData.padding.left + index * groupWidth + xOffset;

                  const weddingHeight =
                    (item.marriage / countyChartData.maxY) * drawableHeight;
                  const divorceHeight =
                    (item.divorce / countyChartData.maxY) * drawableHeight;

                  const weddingY =
                    countyChartData.padding.top +
                    (drawableHeight - weddingHeight);
                  const divorceY =
                    countyChartData.padding.top +
                    (drawableHeight - divorceHeight);

                  const countyLabel =
                    item.county.length > 10
                      ? `${item.county.slice(0, 10)}…`
                      : item.county;

                  return (
                    <g key={item.county}>
                      <rect
                        x={xStart}
                        y={weddingY}
                        width={barWidth}
                        height={Math.max(1, weddingHeight)}
                        fill="#2563eb"
                        rx="2"
                      >
                        <title>{`${item.county} - Marriage: ${item.marriage}`}</title>
                      </rect>
                      <rect
                        x={xStart + barWidth + gap}
                        y={divorceY}
                        width={barWidth}
                        height={Math.max(1, divorceHeight)}
                        fill="#dc2626"
                        rx="2"
                      >
                        <title>{`${item.county} - Divorce: ${item.divorce}`}</title>
                      </rect>
                      {(index % countyChartData.labelStep === 0 ||
                        index === countyChartData.items.length - 1) && (
                        <text
                          x={xStart + barWidth + gap / 2}
                          y={countyChartData.chartHeight - 8}
                          textAnchor="middle"
                          fontSize="10"
                          fill="#64748b"
                        >
                          {countyLabel}
                        </text>
                      )}
                    </g>
                  );
                })}

                <text
                  x={countyChartData.padding.left}
                  y={12}
                  fontSize="10"
                  fill="#64748b"
                >
                  Max/county: {countyChartData.maxY}
                </text>
              </svg>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginTop: "0.5rem",
                gap: "1rem",
                flexWrap: "wrap",
                fontSize: "0.85rem",
              }}
            >
              <div
                style={{ display: "flex", gap: "1rem", alignItems: "center" }}
              >
                <span
                  style={{
                    display: "inline-flex",
                    gap: "0.35rem",
                    alignItems: "center",
                    color: "#1e3a8a",
                  }}
                >
                  <span
                    style={{
                      width: "10px",
                      height: "10px",
                      borderRadius: "2px",
                      background: "#2563eb",
                      display: "inline-block",
                    }}
                  />
                  Marriage
                </span>
                <span
                  style={{
                    display: "inline-flex",
                    gap: "0.35rem",
                    alignItems: "center",
                    color: "#991b1b",
                  }}
                >
                  <span
                    style={{
                      width: "10px",
                      height: "10px",
                      borderRadius: "2px",
                      background: "#dc2626",
                      display: "inline-block",
                    }}
                  />
                  Divorce
                </span>
              </div>
              <span style={{ color: "#64748b" }}>
                {new Date(`${countyRangeStart}T00:00:00`).toLocaleDateString()}{" "}
                - {new Date(`${countyRangeEnd}T00:00:00`).toLocaleDateString()}
              </span>
            </div>
          </article>
        </section>

        <section className="content-grid">
          <article className="panel">
            <h3>Application Pipeline (by status)</h3>
            {statusBreakdown.rows.length === 0 ? (
              <p className="portal-muted">No applications yet.</p>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart
                  data={statusBreakdown.rows}
                  layout="vertical"
                  margin={{ top: 4, right: 16, bottom: 4, left: 8 }}
                >
                  <XAxis type="number" allowDecimals={false} />
                  <YAxis
                    type="category"
                    dataKey="name"
                    width={92}
                    tick={{ fontSize: 12 }}
                  />
                  <Tooltip cursor={{ fill: "rgba(0,0,0,0.04)" }} />
                  <Bar dataKey="count" radius={[0, 6, 6, 0]}>
                    {statusBreakdown.rows.map((row) => (
                      <Cell key={row.name} fill={row.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </article>

          <article className="panel">
            <h3>Revenue Breakdown ({defaultCurrency})</h3>
            {revenueDonut.length === 0 ? (
              <p className="portal-muted">No revenue recorded yet.</p>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={revenueDonut}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={95}
                    paddingAngle={2}
                  >
                    {revenueDonut.map((d) => (
                      <Cell key={d.name} fill={d.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value) => formatCurrency(Number(value))}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            )}
          </article>

          <article className="panel" style={{ gridColumn: "1 / -1" }}>
            <h3>Needs Attention</h3>
            <div className="attention-list">
              <button
                type="button"
                className="attention-item"
                onClick={() => navigate("/marriages/approval")}
              >
                <span className="attention-item__label">
                  ⏳ Awaiting Approval
                </span>
                <span className="attention-item__count">
                  {kpis.awaitingApproval}
                </span>
              </button>
              <button
                type="button"
                className="attention-item"
                onClick={() => navigate("/marriages/approval")}
              >
                <span className="attention-item__label">
                  ❓ Queried (action needed)
                </span>
                <span className="attention-item__count">{kpis.queried}</span>
              </button>
              <button
                type="button"
                className="attention-item"
                onClick={() => navigate("/marriages/finalize")}
              >
                <span className="attention-item__label">
                  🖨️ Ready to Finalize / Print
                </span>
                <span className="attention-item__count">
                  {(reprintWeddingCount ?? 0) + (reprintDivorceCount ?? 0)}
                </span>
              </button>
              <button
                type="button"
                className="attention-item"
                onClick={() => navigate("/reports/applications-summary")}
              >
                <span className="attention-item__label">
                  📊 Full Applications Report
                </span>
                <span className="attention-item__count">→</span>
              </button>
            </div>
          </article>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Dashboard;
